import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';
import { route } from 'preact-router';

import { T, btnIcon, card } from '@/ui/tokens';
import { Icons, sized } from '@/ui/icons';
import { TopBar } from '@/ui/primitives/TopBar';
import { Chip } from '@/ui/primitives/Chip';
import { Plat } from '@/ui/primitives/Plat';
import { CoinGlyph } from '@/ui/primitives/CoinGlyph';
import { Spinner } from '@/ui/primitives/Spinner';
import { ErrorBox } from '@/ui/primitives/ErrorBox';
import { AnimatedList } from '@/ui/primitives/AnimatedList';
import { catalogRepo } from '@/data/repositories/catalog-repo';
import { homeRepo } from '@/data/repositories/home-repo';
import { fmt, fillWindow, queueTime } from '@/ui/utils/format';
import { config, buildRoute } from '@/config';
import { useDragScroll } from '@/ui/primitives/useDragScroll';
import { type Service, type Platform } from '@/domain/types';

function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function sortPlatforms(platforms: Platform[]) {
  return [...platforms].sort((a, b) => {
    if (!!a.isspecial !== !!b.isspecial) return a.isspecial ? -1 : 1;
    return (a.name ?? '').localeCompare(b.name ?? '');
  });
}

type ServicesRestoreState = {
  platformId?: number | null;
  scrollY?: number;
};

const servicesStateKey = 'boostly.services.state.v1';

function readServicesState(): ServicesRestoreState {
  try {
    const raw = sessionStorage.getItem(servicesStateKey);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as ServicesRestoreState;
    return {
      platformId: typeof parsed.platformId === 'number' ? parsed.platformId : null,
      scrollY: typeof parsed.scrollY === 'number' ? parsed.scrollY : 0,
    };
  } catch {
    return {};
  }
}

function writeServicesState(state: ServicesRestoreState) {
  try {
    sessionStorage.setItem(servicesStateKey, JSON.stringify(state));
  } catch {
    // ignore storage failures
  }
}

export function ServicesScreen() {
  const platforms = useQuery({
    queryKey: ['platforms'],
    queryFn: catalogRepo.platforms,
    staleTime: config.catalog.staleTimeMs,
  });
  const home = useQuery({ queryKey: ['home'], queryFn: homeRepo.home });

  const [platformId, setPlatformId] = useState<number | null>(null);
  const [filt, setFilt] = useState('all');
  const [restored, setRestored] = useState(false);

  const [searchOpen, setSearchOpen] = useState(false);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const [query, setQuery] = useState('');
  const dq = useDebounced(query.trim(), 220);
  const searching = searchOpen && dq.length > 0;
  const pendingScrollRef = useRef<number | null>(null);

  const platformScroll = useDragScroll<HTMLDivElement>();
  const filterScroll = useDragScroll<HTMLDivElement>();

  const search = useQuery({
    queryKey: ['service-search', dq],
    queryFn: () => catalogRepo.search(dq),
    enabled: searching,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (!viewAllOpen) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [viewAllOpen]);

  useEffect(() => {
    const state = readServicesState();
    if (state.platformId != null) {
      setPlatformId(state.platformId);
    }
    pendingScrollRef.current = typeof state.scrollY === 'number' ? state.scrollY : null;
    setRestored(true);
  }, []);

  useEffect(() => {
    if (!restored) return;
    if (platforms.isPending || !platforms.data?.length) return;
    if (pendingScrollRef.current == null) return;
    if (searchOpen || viewAllOpen) return;

    const scrollY = pendingScrollRef.current;
    pendingScrollRef.current = null;
    requestAnimationFrame(() => {
      window.scrollTo({ top: Math.max(0, scrollY ?? 0), behavior: 'auto' });
    });
  }, [restored, platforms.isPending, platforms.data, searchOpen, viewAllOpen]);

  useEffect(() => {
    return () => {
      writeServicesState({
        platformId,
        scrollY: window.scrollY,
      });
    };
  }, [platformId]);

  const activePlatform: Platform | undefined = useMemo(() => {
    const list = sortPlatforms(platforms.data ?? []);
    if (platformId == null) return list[0];
    return list.find(p => p.id === platformId) ?? list[0];
  }, [platforms.data, platformId]);

  const services = useQuery({
    queryKey: ['services', activePlatform?.id],
    queryFn: () => catalogRepo.services(activePlatform!.id!),
    enabled: !!activePlatform?.id,
    staleTime: config.catalog.staleTimeMs,
  });

  const categories = useQuery({
    queryKey: ['categories', activePlatform?.id],
    queryFn: () => catalogRepo.categories(activePlatform!.id!),
    enabled: !!activePlatform?.id,
    staleTime: config.catalog.staleTimeMs,
  });

  const filtered = useMemo(() => {
    const list = services.data ?? [];
    if (filt === 'all') return list;
    return list.filter(s => (s.category ?? '').toLowerCase() === filt.toLowerCase());
  }, [services.data, filt]);

  return (
    <div style={{ background: T.bg, minHeight: '100%' }}>
      <TopBar title="Services" right={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            style={viewAllButton}
            onClick={() => setViewAllOpen(true)}
            aria-label="View all platforms"
          >
            {sized(Icons.list, 15)}
            <span>View all</span>
          </button>
          <button style={btnIcon} aria-label="Search"
            onClick={() => { const next = !searchOpen; setSearchOpen(next); if (!next) setQuery(''); }}>
            {sized(searchOpen ? Icons.x : Icons.search, 18)}
          </button>
        </div>
      }/>

      {searchOpen && (
        <div style={{ padding: '4px 20px 8px' }}>
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: 12, color: T.textMute, pointerEvents: 'none' }}>
              {sized(Icons.search, 16)}
            </span>
            <input
              autoFocus
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              placeholder="Search all services…"
              style={{
                width: '100%', boxSizing: 'border-box', padding: '11px 36px 11px 36px',
                borderRadius: 12, background: T.surface, border: `1px solid ${T.line}`,
                color: T.text, fontFamily: T.font, fontSize: 14, outline: 'none',
              }}
            />
            {query && (
              <button onClick={() => setQuery('')} aria-label="Clear"
                style={{ position: 'absolute', right: 8, top: 8, width: 24, height: 24,
                  borderRadius: 8, background: 'transparent', border: 'none', color: T.textMute,
                  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                {sized(Icons.x, 14)}
              </button>
            )}
          </div>
        </div>
      )}

      {searching ? (
        <div style={{ padding: '8px 20px 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {search.isPending ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner/></div>
          ) : search.isError ? (
            <ErrorBox message="Search failed." onRetry={() => search.refetch()}/>
          ) : (search.data ?? []).length === 0 ? (
            <div style={{ textAlign: 'center', padding: 30, color: T.textDim, fontSize: 13 }}>
              No services match “{dq}”.
            </div>
          ) : (search.data ?? []).map(s => <ServiceCard key={s.id} s={s} showPlatform/>)}
        </div>
      ) : (
      <>
      {platforms.isError && <ErrorBox message="Couldn't load platforms." onRetry={() => platforms.refetch()}/>}

      <div style={{ padding: '14px 0 8px' }}>
        <div style={{ padding: '0 20px 4px', fontSize: 11, color: T.textMute,
          textTransform: 'uppercase', letterSpacing: 1.2, fontWeight: 500 }}>Platform</div>
        <div ref={platformScroll} style={{ display: 'flex', gap: 10, overflowX: 'auto', padding: '8px 20px', userSelect: 'none', scrollbarWidth: 'none', cursor: 'grab' }}>
          <AnimatedList
            items={sortPlatforms(platforms.data ?? [])}
            keyFn={p => p.id ?? 0}
            renderItem={(p) => {
              const on = p.id === activePlatform?.id;
              return (
                <button onClick={() => {
                  setPlatformId(p.id!);
                  setFilt('all');
                  writeServicesState({ platformId: p.id!, scrollY: window.scrollY });
                }} style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  gap: 8, padding: '12px 14px', minWidth: 84, borderRadius: 18,
                  border: '1px solid', borderColor: on ? T.accent : T.line,
                  background: on ? 'rgba(198,255,61,0.08)' : T.surface,
                  cursor: 'pointer', flexShrink: 0, position: 'relative',
                }}>
                  <Plat name={p.name?.toLowerCase() ?? ''} size={28} logourl={p.logourl}/>
                  <span style={{ fontSize: 12, fontWeight: on ? 600 : 500,
                    color: on ? T.accent : T.text, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                    {p.isspecial && <span aria-hidden="true" style={{ color: T.accent }}>★</span>}
                    {p.name}
                  </span>
                  {p.suspendeduntil && (
                    <span style={{ position: 'absolute', top: 6, right: 6, padding: '1px 5px',
                      borderRadius: 4, background: T.warn, color: T.accentInk,
                      fontSize: 8, fontWeight: 700 }}>PAUSED</span>
                  )}
                </button>
              );
            }}
            direction="row" gap={10} batchSize={30}
          />
        </div>
      </div>

      <div style={{
        margin: '0 20px 14px', padding: '14px 16px', borderRadius: 16,
        background: T.surface, border: `1px solid ${T.line}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <CoinGlyph size={20}/>
          <div>
            <div style={{ fontSize: 10, color: T.textMute, textTransform: 'uppercase',
              letterSpacing: 1, fontWeight: 500 }}>Wallet</div>
            <div style={{ fontFamily: T.fontMono, fontSize: 16, color: T.text,
              fontVariantNumeric: 'tabular-nums', fontWeight: 500, marginTop: 1 }}>
              {fmt(home.data?.coins ?? 0)} <span style={{ color: T.accent, fontSize: 11 }}>C</span>
            </div>
          </div>
        </div>
        <button style={{
          padding: '8px 14px', borderRadius: 10, background: T.accent,
          color: T.accentInk, border: 'none', fontSize: 12, fontWeight: 600, cursor: 'pointer',
        }}>Top up</button>
      </div>

      <div ref={filterScroll} style={{ display: 'flex', gap: 8, padding: '0 20px 14px', overflowX: 'auto', userSelect: 'none', scrollbarWidth: 'none', cursor: 'grab' }}>
        <Chip active={filt === 'all'} onClick={() => setFilt('all')}>All</Chip>
        {(categories.data ?? []).map(c => (
          <Chip key={c.name} active={(c.name ?? '').toLowerCase() === filt.toLowerCase()} onClick={() => setFilt(c.name ?? '')}>
            {capitalize(c.name ?? '')}
          </Chip>
        ))}
      </div>

      <div style={{ padding: '0 20px' }}>
        {services.isPending ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 30 }}><Spinner/></div>
        ) : services.isError ? (
          <ErrorBox message="Couldn't load services." onRetry={() => services.refetch()}/>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: T.textDim, fontSize: 13 }}>
            No services match this filter.
          </div>
        ) : (
          <AnimatedList
            items={filtered}
            keyFn={s => s.id ?? 0}
            renderItem={s => <ServiceCard s={s}/>}
            batchSize={15} staggerMs={35}
          />
        )}
      </div>
      </>
      )}

      {viewAllOpen && (
        <PlatformGridModal
          platforms={sortPlatforms(platforms.data ?? [])}
          activeId={activePlatform?.id}
          isPending={platforms.isPending}
          isError={platforms.isError}
          onRetry={() => platforms.refetch()}
          onSelect={(id) => {
            setPlatformId(id);
            setFilt('all');
            setSearchOpen(false);
            setQuery('');
            setViewAllOpen(false);
            writeServicesState({ platformId: id, scrollY: window.scrollY });
          }}
          onClose={() => {
            setViewAllOpen(false);
          }}
        />
      )}
    </div>
  );
}

function titleCase(s: string) {
  return s.replace(/\b\w/g, c => c.toUpperCase());
}

function ServiceCard({ s, showPlatform }: { s: Service; showPlatform?: boolean }) {
  return (
    <div style={{
      ...card, padding: 16, display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        width: 44, height: 44, borderRadius: 14,
        background: 'rgba(198,255,61,0.10)', border: `1px solid rgba(198,255,61,0.20)`,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        color: T.accent, flexShrink: 0,
      }}>{sized(Icons.spark, 22)}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {showPlatform && s.platform && (
          <div style={{ fontSize: 10, color: T.accent, textTransform: 'uppercase',
            letterSpacing: 0.8, fontWeight: 600, marginBottom: 2 }}>{s.platform}</div>
        )}
        <div style={{ fontSize: 10, fontWeight: 600, color: T.text,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          marginBottom: 4 }}>{titleCase(s.name ?? '')}</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 10px', fontSize: 11,
          color: T.textDim, fontFamily: T.fontMono, fontVariantNumeric: 'tabular-nums' }}>
          <span>ID {s.id}</span>
          <span style={{ color: T.line2 }}>•</span>
          <span>Min {fmt(s.min ?? 0)}</span>
          <span style={{ color: T.line2 }}>•</span>
          <span>Max {fmt(s.max ?? 0)}</span>
          {!!s.filltime && (
            <>
              <span style={{ color: T.line2 }}>•</span>
              <span>{fillWindow(s.filltime)} fill</span>
            </>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 6 }}>
          <CoinGlyph size={12}/>
          <span style={{ fontFamily: T.fontMono, fontSize: 13, color: T.text,
            fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{s.costper}</span>
          <span style={{ fontSize: 11, color: T.textMute }}>per unit</span>
          {s.name?.toLowerCase().includes('refill') && (
            <span style={{ fontSize: 11, color: T.textMute, marginLeft: 4 }}>♻️ Refill</span>
          )}
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0 }}>
        <button onClick={() => route(buildRoute.buy(s.id!))} style={{
          padding: '10px 16px', borderRadius: 12, background: T.accent,
          color: T.accentInk, border: 'none', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        }}>Buy</button>
        {s.avgqueuetime != null && (
          <span title="Average queue time" style={{ fontSize: 12, color: T.textDim, fontFamily: T.fontMono,
            whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>⏱ {queueTime(s.avgqueuetime)}</span>
        )}
      </div>
    </div>
  );
}

function PlatformGridModal({
  platforms, activeId, isPending, isError, onRetry, onSelect, onClose,
}: {
  platforms: Platform[];
  activeId?: number;
  isPending: boolean;
  isError: boolean;
  onRetry: () => void;
  onSelect: (id: number) => void;
  onClose: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="All platforms"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 100,
        background: 'rgba(0,0,0,0.58)', color: T.text,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 'calc(var(--safe-top, 0px) + 18px) 18px calc(var(--safe-bottom, 0px) + 18px)',
      }}
    >
      <div style={{
        width: 'min(100%, 520px)', maxHeight: '82vh', overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
        background: T.bg, border: `1px solid ${T.line2}`, borderRadius: 18,
        boxShadow: '0 24px 80px rgba(0,0,0,0.38)',
      }} onClick={(e) => e.stopPropagation()}>
        <div style={{
          padding: '16px 16px 12px',
          borderBottom: `1px solid ${T.line}`,
          background: T.bg,
        }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: T.text }}>Platforms</div>
            <div style={{ fontSize: 12, color: T.textDim, marginTop: 2 }}>
              {isPending ? 'Loading catalog' : `${fmt(platforms.length)} available`}
            </div>
          </div>
          <button onClick={onClose} style={btnIcon} aria-label="Close">
            {sized(Icons.x, 18)}
          </button>
        </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 16 }}>
        {isPending ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: 34 }}><Spinner/></div>
        ) : isError ? (
          <ErrorBox message="Couldn't load platforms." onRetry={onRetry}/>
        ) : platforms.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: T.textDim, fontSize: 13 }}>
            No platforms available.
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
            gap: 10,
          }}>
            {platforms.map(p => {
              const on = p.id === activeId;
              return (
                <button
                  key={p.id}
                  onClick={() => p.id && onSelect(p.id)}
                  style={{
                    minHeight: 104, padding: '12px 8px', borderRadius: 14,
                    border: '1px solid', borderColor: on ? T.accent : T.line,
                    background: on ? 'rgba(198,255,61,0.10)' : T.surface,
                    color: on ? T.accent : T.text,
                    cursor: 'pointer', fontFamily: T.font,
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', gap: 9, position: 'relative',
                  }}
                >
                  <Plat name={p.name?.toLowerCase() ?? ''} size={34} logourl={p.logourl}/>
                  <span style={{
                    width: '100%', minHeight: 28, display: 'inline-flex',
                    alignItems: 'center', justifyContent: 'center', gap: 4,
                    fontSize: 12, lineHeight: 1.15, fontWeight: on ? 700 : 600,
                    textAlign: 'center', overflowWrap: 'anywhere',
                  }}>
                    {p.isspecial && <span aria-hidden="true">★</span>}
                    {p.name}
                  </span>
                  {on && (
                    <span style={{
                      position: 'absolute', top: 8, right: 8,
                      width: 16, height: 16, borderRadius: 6,
                      background: T.accent, color: T.accentInk,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {sized(Icons.check, 11, 2.4)}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
}

const viewAllButton = {
  height: 36,
  padding: '0 11px',
  borderRadius: T.radiusSm,
  background: T.surface,
  border: `1px solid ${T.line}`,
  color: T.text,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  cursor: 'pointer',
  fontFamily: T.font,
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
} as const;

// Delays a fast-changing value so search hits the API only after typing settles.
function useDebounced<T>(value: T, delayMs: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return v;
}
